import React, { useState, useContext, useEffect } from "react";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import { AppContext, DispatchContext } from "../AppContext";
import { makeStyles } from "@material-ui/styles";
import DateFnsUtils from "@date-io/date-fns";
import { Button, IconButton, List, ListItem, TextField, MenuItem } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import * as actions from '../actions/actions';
import Service from "../services/service";
import { compareIgnoreEmpty, resolveNotificationMessage, trimEmptyLines } from "../helpers/constants";

const useStyles = makeStyles((theme) => ({
    root: {
        overflow: "hidden",
        flexDirection: "column",
        display: "flex",
        overflowY: "auto",
        padding: 0,
        width: '100%'
    },
    date_format: {
        padding: "0 !important",
    },
    text_filed_container: {
        width: '100%'
    },
    button_block: {
        width: '100%',
        display: 'none'
    },
    form: {
        width: "100%",
        marginTop: theme.spacing(1),
    },
    width_80: {
        width: '80%'
    },
    addProject: {
        marginTop: "16px",
        textAlign: "right",
        width: "20%",
        cursor: "Pointer"
    }
}));

export default function LibraryPropertiesForm(props) {
    const dispatch = useContext(DispatchContext);
    const state = useContext(AppContext);
    const s = state.strings;
    const classes = useStyles(state);
    const { library, validation } = props;
    const [formData, setFormData] = useState(convertToFormData(library));

    // update the local props when library has been updated
    useEffect(() => {
        resetFormData();
    }, [JSON.stringify(library)]);

    function resetFormData() {
        setFormData(convertToFormData(library));
    }

    // convert data as received from API into values suitable to form
    function convertToFormData(library) {
        const formData = {
            Date: library.Date ? new Date(parseInt(library.Date * 1000)) : new Date(),
            Name: library.Name || "",
            Comment: library.Comment || "",
            Author: library.Author || "",
            ProjectId: library.ProjectId || ""
        }
        return formData;
    }

    // convert form values into values suitable to API
    function convertToSubmitData(formData) {
        const submitData = {
            Date: new Date(formData.Date).getTime() / 1000,
            Name: trimEmptyLines(formData.Name) || null,
            Comment: trimEmptyLines(formData.Comment) || null,
            Author: trimEmptyLines(formData.Author) || null,
            ProjectId: parseInt(formData.ProjectId) || null
        }
        return submitData;
    }

    const onConfirmUpdate = (submitData, changedField) => {
        state.selectedLibraryIds.length && Service.UpdateLibraries({ ...submitData, field: changedField }, state.selectedLibraryIds).then(res => {
            if (res.success) {
                Service.getLibrary(library.Id).then(res => {
                    dispatch(actions.get_library(res.data || []));
                    dispatch(actions.notifyLibrariesChanged());
                });
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage) }));
                resetFormData();
            }
        });
    }

    const onCancelUpdate = () => {
        resetFormData();
    }

    function getChangedField(submitData) {
        const savedData = library;
        let changedField = null;
        changedField = compareIgnoreEmpty(savedData.Name, submitData.Name, "name") || changedField;
        changedField = compareIgnoreEmpty(savedData.Date, submitData.Date, "date") || changedField;
        changedField = compareIgnoreEmpty(savedData.Author, submitData.Author, "author") || changedField;
        changedField = compareIgnoreEmpty(savedData.ProjectId, submitData.ProjectId, "projectid") || changedField;
        changedField = compareIgnoreEmpty(savedData.Comment, submitData.Comment, "comment") || changedField;
        return changedField;
    }


    // form has been submitted, e.g. by hitting [enter] which triggered the form's submit button OR by a blur event of a control
    const onFormSubmitted = (e, type, key) => {
        e.preventDefault();

        // find out if and what data has been changed, so that we can only trigger the backend update for the changed field
        // this is particularly important if multiple items had been selected to prevent changing all values for all of them 
        const submitData = convertToSubmitData(formData);   // converting to submitData removes unneccessary spaces etc.
        const changedField = getChangedField(submitData);
        if (!changedField) {
            resetFormData();                                // nothing has changed, revert the form data, cleaning unneccessary spaces etc.
            return;
        }

        // validate the form and revert entries if validation fails
        if (!validateForm(submitData)) {
            resetFormData();
            return;
        }

        submitForm(submitData, changedField);
    }

    function validateForm(submitData) {
        if (!submitData.Name) {
            dispatch(actions.openToast({ type: "error", message: s.notificationMessage.titleRequired, status: true }));
            return false;
        }
        return true;
    }

    function submitForm(submitData, changedField) {
        if (state.selectedLibraryIds.length > 1) {
            dispatch(actions.openConfirmationDialog({
                title: s.libraryOverview.updateTexts.format(state.selectedLibraryIds.length),
                text: s.libraryOverview.updateTextsDesc,
                onConfirm: () => onConfirmUpdate(submitData, changedField),
                onCancel: () => onCancelUpdate(),
                hint: s.libraryOverview.updateTextsHint
            }));
        } else {
            onConfirmUpdate(submitData, changedField);
        }

    }

    const createProjectPopup = () => {
        dispatch(actions.create_project_popup(true));
    }

    return (

        <div className={classes.root}>
            <form className={classes.form} noValidate autoComplete="off" onSubmit={onFormSubmitted}>
                <List>
                    {library.Id ?
                        <ListItem>
                            <div className={classes.text_filed_container}>
                                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                    <DatePicker
                                        disabled = {!library.IsOwner}
                                        required
                                        label={s.date}
                                        size="small"
                                        format={s.common.dateFormatJS}
                                        className={`${classes.text_filed_container} ${classes.date_format}`}
                                        value={formData.Date}
                                        onBlur={(e) => onFormSubmitted(e, 'Scheduled', 'date')}
                                        onChange={(date) => setFormData({ ...formData, Date: date })}
                                    />
                                </MuiPickersUtilsProvider>
                            </div>
                        </ListItem>
                        : ""}
                    <ListItem>
                        <div className={classes.text_filed_container}>
                            <TextField
                                disabled = {!library.IsOwner}
                                className={classes.text_filed_container}
                                size="small"
                                label={s.title}
                                value={formData.Name}
                                onBlur={(e) => onFormSubmitted(e, 'Name', 'name')}
                                onChange={(e) => setFormData({ ...formData, Name: e.target.value })
                                }
                            />
                        </div>
                    </ListItem>
                    <ListItem>
                        <div className={classes.text_filed_container}>
                            <TextField
                                disabled = {!library.IsOwner}
                                className={classes.text_filed_container}
                                label={s.author}
                                size="small"
                                value={formData.Author}
                                InputProps={{ endAdornment: "" }}
                                onBlur={(e) => onFormSubmitted(e, 'Author', 'author')}
                                onChange={(e) => setFormData({ ...formData, Author: e.target.value })
                                }
                            />
                        </div>
                    </ListItem>
                    <ListItem>
                        <div className={classes.width_80}>
                            {library.IsOwner ? 
                                <TextField
                                    disabled = {!library.IsOwner}
                                    size="small"
                                    select
                                    className={classes.text_filed_container}
                                    label={s.project}
                                    value={formData.ProjectId > 0 ? formData.ProjectId : ""}
                                    onBlur={(e) => onFormSubmitted(e, 'ProjectId', 'projectid')}
                                    onChange={(e) => setFormData({ ...formData, ProjectId: e.target.value })}>
                                    <MenuItem key={0} value={0}>
                                        [{s.noProject}]
                                    </MenuItem>
                                    {state.projects.filter(option => option.IsOwner || option.AllowAdd)
                                        .map((option) => (
                                            <MenuItem key={option.Id} value={option.Id}>
                                                {option.Name}
                                            </MenuItem>
                                        ))
                                    }
                                </TextField>
                            :
                                <TextField
                                    disabled
                                    size="small"
                                    className={classes.text_filed_container}
                                    label={s.project}
                                    value={library.ProjectName || ""}
                                />
                            }

                        </div>
                        <div className={classes.addProject}>
                            <IconButton disabled = {!library.IsOwner} onClick={() => { createProjectPopup(); }}>
                                <AddIcon />
                            </IconButton>
                        </div>
                    </ListItem>
                    <ListItem>
                        <div className={classes.text_filed_container}>
                            <TextField
                                disabled = {!library.IsOwner}
                                multiline
                                className={classes.text_filed_container}
                                size="small"
                                label={s.notes}
                                value={formData.Comment}
                                onBlur={(e) => onFormSubmitted(e, 'Comment', 'comment')}
                                onChange={(e) => setFormData({ ...formData, Comment: e.target.value })
                                }
                            />
                        </div>
                    </ListItem>
                </List>

                <Button style={{ display: "none" }} type="submit">
                    {s.submit}
                </Button>
                {/* List item end */}
            </form>
        </div>
    );
};
